import React, { createContext, useContext, ComponentProps, ReactNode, ReactElement } from 'react';
import clamp from 'lodash/clamp';
import useWindowScroll from 'react-use/lib/useWindowScroll';
import styled, { css, CSSProperties } from 'styled-components';
import { BreakpointProp } from '../../utils/styles/breakpoints';
import { IconButton } from '../input/IconButton';
import { Box } from '../layout/Box';
import { Flex, FlexProps } from '../layout/Flex';

const ToolbarContext = createContext<number>(0);

const ToolbarContainer = styled(Box).attrs((props) => ({
    ...props,
    position: props.position ?? { xs: 'sticky', md: 'relative' },
}))`
    z-index: 10;
    top: var(--safe-area-inset-top);
`;

interface ToolbarInnerProps {
    transparent: boolean;
}

const ToolbarInner = styled(Flex)
    .attrs<ToolbarInnerProps, FlexProps & ToolbarInnerProps>({
        py: { xs: 1, md: 1.5 },
        px: { xs: 1.5, md: 2 },
        zIndex: 100,
        position: 'relative',
        spacing: 1,
        width: '100%',
        direction: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    })
    .withConfig({
        displayName: 'ToolbarInner',
        shouldForwardProp: (prop) => !['transparent'].includes(prop),
    })`
    background-color: var(--background-primary);
    -webkit-backdrop-filter: blur(12px);
    backdrop-filter: blur(12px);
    transition-property: background-color, backdrop-filter;
    transition-duration: 250ms;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);

    ${(props) =>
        props.transparent &&
        css`
            background-color: transparent;
            -webkit-backdrop-filter: none;
            backdrop-filter: none;
        `}
`;

interface ToolbarPayload {
    isStacked: boolean;
}

interface ToolbarProps {
    position?: BreakpointProp<CSSProperties['position']>;
    animationDistance?: number;
    children: ReactNode | ((payload: ToolbarPayload) => ReactElement);
}

export const Toolbar = (props: ToolbarProps) => {
    const { position, children, animationDistance = 0 } = props;
    const windowScroll = useWindowScroll();
    const portion = animationDistance > 0 ? clamp(windowScroll.y / animationDistance, 0, 1) : 1;

    return (
        <ToolbarContext.Provider value={portion}>
            <ToolbarContainer height={animationDistance > 0 ? 0 : 'auto'} position={position}>
                <ToolbarInner transparent={portion < 1}>
                    {typeof children === 'function'
                        ? children({ isStacked: portion === 1 })
                        : children}
                </ToolbarInner>
            </ToolbarContainer>
        </ToolbarContext.Provider>
    );
};

const Ripple = styled.div`
    position: absolute;
    border-radius: 50%;
    width: 100%;
    height: 100%;
    background: color-mix(in srgb, var(--background-primary) 40%, transparent);
    -webkit-backdrop-filter: blur(20px);
    backdrop-filter: blur(20px);
    will-change: transform, opacity;
    z-index: -1;
`;

export const ToolbarIconButton = (props: ComponentProps<typeof IconButton>) => {
    const { children, ...other } = props;
    const portion = useContext(ToolbarContext);

    return (
        <IconButton color="primary" {...other}>
            {portion < 1 && (
                <Ripple style={{ transform: `scale(${1 - portion / 4})`, opacity: 1 - portion }} />
            )}
            {children}
        </IconButton>
    );
};
